import React, { useState } from "react";
import {
  Box,
  Typography,
  Sheet,
  Button,
  Table,
  IconButton,
  CircularProgress,
  Alert,
  Chip,
  Modal,
  ModalDialog,
  ModalClose,
  Stack,
  Tooltip,
  Card,
  CardContent,
  Grid,
} from "@mui/joy";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import TableChartIcon from "@mui/icons-material/TableChart";
import { useNavigate } from "react-router-dom";
import { useWorkspaceContext } from "../../../providers/WorkspaceProvider";
import { useViews, useViewMutations } from "../hooks";
import type { ViewDefinition } from "../types";
import { getViewJoinCount } from "./viewJoinCount";

/**
 * ViewBrowser - Browse and discover data views
 */
export default function ViewBrowser() {
  const navigate = useNavigate();
  const { workspace } = useWorkspaceContext();
  const workspaceId = workspace?.id || "";

  const { views, loading, error, refresh } = useViews(workspaceId);
  const { deleteView, loading: deleting } = useViewMutations(workspaceId);

  const [deleteConfirm, setDeleteConfirm] = useState<ViewDefinition | null>(
    null,
  );
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [viewMode] = useState<"list" | "grid">("list");

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      await deleteView(deleteConfirm.id);
      setDeleteConfirm(null);
      setDeleteError(null);
      refresh();
    } catch (err) {
      setDeleteError(
        err instanceof Error ? err.message : "Failed to delete view",
      );
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  if (!workspaceId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert color="warning">Please select a workspace first.</Alert>
      </Box>
    );
  }

  const renderViewCard = (view: ViewDefinition) => (
    <Card key={view.id} variant="outlined" sx={{ height: "100%" }}>
      <CardContent>
        <Stack spacing={1}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="flex-start"
          >
            <Typography
              level="title-md"
              sx={{ cursor: "pointer", "&:hover": { color: "primary.500" } }}
              onClick={() => navigate(`${view.id}`)}
            >
              {view.name}
            </Typography>
            <Stack direction="row" spacing={0.5}>
              <Tooltip title="Edit view">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="neutral"
                  onClick={() => navigate(`${view.id}/edit`)}
                >
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete view">
                <IconButton
                  size="sm"
                  variant="plain"
                  color="danger"
                  onClick={() => setDeleteConfirm(view)}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Stack>
          <Typography level="body-sm" sx={{ color: "text.secondary" }}>
            {view.description || "No description"}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip size="sm" variant="soft" color="primary">
              {view.field_count ?? view.fields?.length ?? 0} fields
            </Chip>
            <Chip size="sm" variant="soft" color="neutral">
              {getViewJoinCount(view)} joins
            </Chip>
          </Stack>
          <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
            Created {formatDate(view.created_at)}
          </Typography>
        </Stack>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography level="h2">Views</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <IconButton
              variant="outlined"
              color="neutral"
              onClick={refresh}
              disabled={loading}
            >
              <RefreshIcon />
            </IconButton>
          </Tooltip>
          <Button
            startDecorator={<AddIcon />}
            color="primary"
            onClick={() => navigate("create")}
          >
            New View
          </Button>
        </Stack>
      </Box>

      {error && (
        <Alert color="danger" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress />
        </Box>
      ) : views.length === 0 ? (
        <Sheet
          variant="outlined"
          sx={{
            borderRadius: "md",
            p: 4,
            textAlign: "center",
          }}
        >
          <ViewModuleIcon
            sx={{ fontSize: 48, color: "text.tertiary", mb: 2 }}
          />
          <Typography level="body-lg" sx={{ color: "text.secondary" }}>
            No views defined yet.
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.tertiary", mt: 1 }}>
            Create views to define how your data is displayed and edited.
          </Typography>
        </Sheet>
      ) : viewMode === "grid" ? (
        <Grid container spacing={2}>
          {views.map((view) => (
            <Grid key={view.id} xs={12} sm={6} md={4}>
              {renderViewCard(view)}
            </Grid>
          ))}
        </Grid>
      ) : (
        <Sheet variant="outlined" sx={{ borderRadius: "md", overflow: "auto" }}>
          <Table
            stripe="odd"
            hoverRow
            sx={{
              "& thead th": { bgcolor: "background.level1" },
              tableLayout: "auto",
            }}
          >
            <thead>
              <tr>
                <th style={{ width: "25%" }}>Name</th>
                <th style={{ width: "30%" }}>Description</th>
                <th style={{ width: "10%" }}>Fields</th>
                <th style={{ width: "10%" }}>Joins</th>
                <th style={{ width: "15%" }}>Created</th>
                <th style={{ width: "10%" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {views.map((view) => (
                <tr key={view.id}>
                  <td>
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <TableChartIcon
                        fontSize="small"
                        sx={{ color: "primary.500" }}
                      />
                      <Typography
                        level="body-sm"
                        fontWeight="md"
                        sx={{
                          cursor: "pointer",
                          "&:hover": { color: "primary.500" },
                        }}
                        onClick={() => navigate(`${view.id}`)}
                      >
                        {view.name}
                      </Typography>
                    </Stack>
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      sx={{ color: "text.secondary" }}
                    >
                      {view.description || "-"}
                    </Typography>
                  </td>
                  <td>
                    <Chip size="sm" variant="soft" color="primary">
                      {view.field_count ?? view.fields?.length ?? 0}
                    </Chip>
                  </td>
                  <td>
                    <Chip size="sm" variant="soft" color="neutral">
                      {getViewJoinCount(view)}
                    </Chip>
                  </td>
                  <td>
                    <Typography
                      level="body-sm"
                      sx={{ color: "text.secondary" }}
                    >
                      {formatDate(view.created_at)}
                    </Typography>
                  </td>
                  <td>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Open view">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="primary"
                          onClick={() => navigate(`${view.id}`)}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit view">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="neutral"
                          onClick={() => navigate(`${view.id}/edit`)}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete view">
                        <IconButton
                          size="sm"
                          variant="plain"
                          color="danger"
                          onClick={() => setDeleteConfirm(view)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Sheet>
      )}

      {/* Delete Confirmation Modal */}
      <Modal open={!!deleteConfirm} onClose={() => setDeleteConfirm(null)}>
        <ModalDialog>
          <ModalClose />
          <Typography level="h4">Delete View</Typography>
          <Typography level="body-md" sx={{ mt: 1 }}>
            Are you sure you want to delete the view &quot;{deleteConfirm?.name}
            &quot;?
          </Typography>
          <Typography level="body-sm" sx={{ color: "text.secondary", mt: 1 }}>
            This will remove the view definition. Data in the underlying tables
            will not be affected.
          </Typography>
          {deleteError && (
            <Alert color="danger" sx={{ mt: 2 }}>
              {deleteError}
            </Alert>
          )}
          <Stack
            direction="row"
            spacing={1}
            sx={{ mt: 3, justifyContent: "flex-end" }}
          >
            <Button
              variant="plain"
              color="neutral"
              onClick={() => setDeleteConfirm(null)}
            >
              Cancel
            </Button>
            <Button color="danger" onClick={handleDelete} loading={deleting}>
              Delete
            </Button>
          </Stack>
        </ModalDialog>
      </Modal>
    </Box>
  );
}
